/**
 * art-personalize.js
 * ------------------------------------------------------------
 * Syncs variant-derived classes onto #art-container
 *
 * - Adds exactly one: surface-0 | surface-1  (Soporte: fieldset 0)
 * - Adds exactly one: variant-{surface}-{frameToken} (Marco: fieldset 1)
 * - Ignores size (fieldset 2)
 *
 * Requirements in DOM:
 * - <variant-picker> with fieldsets:
 *    - data-fieldset-index="0" => Soporte (poster/lienzo)
 *    - data-fieldset-index="1" => Marco (frame)
 * - #art-container exists and wraps everything you want to style
 *
 * Include with:
 *   <script src="{{ 'art-personalize.js' | asset_url }}" defer></script>
 */

(() => {
  const SELECTOR_PICKER = 'variant-picker';
  const SELECTOR_ART = '#art-container';

  // --- small helpers ---
  const isRadio = (el) =>
    el && el.tagName === 'INPUT' && el.type === 'radio';

  // Convert 1..9 => "1".."9", 10=>"A", 11=>"B"... (supports beyond E if needed)
  const toFrameToken = (oneBased) => {
    if (oneBased <= 9) return String(oneBased);
    return String.fromCharCode('A'.charCodeAt(0) + (oneBased - 10));
  };

  const getCheckedInFieldset = (root, fieldsetIndex) =>
    root.querySelector(
      `fieldset[data-fieldset-index="${fieldsetIndex}"] input[type="radio"]:checked`
    );

  // Remove classes by prefix, but only those we manage.
  const removePrefixedClasses = (el, prefixes) => {
    // Iterate over a snapshot to safely remove
    [...el.classList].forEach((c) => {
      for (const p of prefixes) {
        if (c.startsWith(p)) {
          el.classList.remove(c);
          break;
        }
      }
    });
  };

  const computeState = (pickerEl) => {
    const support = getCheckedInFieldset(pickerEl, 0); // Soporte
    const frame = getCheckedInFieldset(pickerEl, 1);   // Marco
    if (!support || !frame) return null;

    // surface is 0/1 from data-input-index (your HTML already provides it)
    const surfaceIdx = Number(support.dataset.inputIndex || 0);

    // frame index: data-input-index is 0-based; we need 1-based -> token
    const frameOneBased = Number(frame.dataset.inputIndex || 0) + 1;
    const frameToken = toFrameToken(frameOneBased);

    return { surfaceIdx, frameToken };
  };

  const applyState = (artEl, state) => {
    // Only touch classes we own
    removePrefixedClasses(artEl, ['surface-', 'variant-']);

    artEl.classList.add(`surface-${state.surfaceIdx}`);
    artEl.classList.add(`variant-${state.surfaceIdx}-${state.frameToken}`);

    // Optional: expose raw state for debugging / CSS attr selectors
    // (No perf impact, but you can remove if you want)
    artEl.dataset.surface = String(state.surfaceIdx);
    artEl.dataset.frame = String(state.frameToken);
  };

  const sync = () => {
    const artEl = document.querySelector(SELECTOR_ART);
    if (!artEl) return;

    const pickerEl = document.querySelector(SELECTOR_PICKER);
    if (!pickerEl) return;

    const state = computeState(pickerEl);
    if (!state) return;

    // Skip if unchanged (avoid any classList work)
    const key = `${state.surfaceIdx}-${state.frameToken}`;
    if (artEl.dataset.variantKey === key) return;
    artEl.dataset.variantKey = key;

    applyState(artEl, state);
  };

  // --- init (script is expected to be loaded with `defer`) ---
  // defer guarantees DOM is parsed; we can run immediately.
  sync();

  // --- updates ---
  // 1) One delegated change listener inside the picker
  document.addEventListener(
    'change',
    (e) => {
      const t = e.target;
      if (!isRadio(t)) return;

      const picker = t.closest(SELECTOR_PICKER);
      if (!picker) return;

      // Let the theme update checked states if it does any extra work
      queueMicrotask(sync);
    },
    true
  );

  // 2) Theme editor re-render
  document.addEventListener('shopify:section:load', () => queueMicrotask(sync));

  // 3) Optional: if your theme emits custom events, these won't hurt
  document.addEventListener('variant:change', () => queueMicrotask(sync));
  document.addEventListener('variant:updated', () => queueMicrotask(sync));
})();
